import { CompanyConsumption } from '@amplify3/duplo-back-end'
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  SORT_ASCENDING,
  SORT_DESCENDING,
} from '../../components/TaskList/JobListDef'

export interface User {
  company_id: string
  user_cognito_id: string
  user_hubspot_id: string
  user_email: string
  user_firstname: string
  user_lastname: string
  role: string
  access_role: string
  user_stripe_id: string | undefined
  creation_date: string
}

export interface ServiceInfo {
  [key: string]: {
    service_name: string
    unit_cost: number
    authorized_tiers: any
    commercial_name: string
    abbreviation: string
  }
}

export interface Webhook {
  company_id: string
  id: string
  name: string
  url: string
}

interface WebhookLog {
  id: string
  webhook_id: string
  job_id: string
  company_id: string
  service_name: string
  status: string
  message: string
  date: string
  duration_call: number
}

export interface Country {
  code: string
  name: string
  isEU: boolean
  currency: 'USD' | 'EUR'
}

interface UserState {
  user?: User
  companyConsumption?: CompanyConsumption
  companyKpi?: any
  // TODO: must create a class or type for Job
  jobs: any[]
  jobsSortDirection: typeof SORT_ASCENDING | typeof SORT_DESCENDING
  lastJobId?: string
  servicesInfo?: ServiceInfo
  appClients?: any[]
  webhooks: Webhook[]
  webhookLogs: WebhookLog[]
  webhookLogsSortDirection: typeof SORT_ASCENDING | typeof SORT_DESCENDING
  lastWebhookLogId?: string
  allowedCountries: Country[]
}

const initialState: UserState = {
  user: undefined,
  companyConsumption: undefined,
  companyKpi: undefined,
  jobs: [],
  jobsSortDirection: SORT_DESCENDING,
  lastJobId: undefined,
  servicesInfo: undefined,
  appClients: undefined,
  webhooks: [],
  webhookLogs: [],
  webhookLogsSortDirection: SORT_DESCENDING,
  lastWebhookLogId: undefined,
  allowedCountries: [],
}

export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.user>
    ) => {
      state.user = action.payload
    },
    setCompanyConsumption: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyConsumption>
    ) => {
      state.companyConsumption = action.payload
    },
    setCompanyKpi: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyKpi>
    ) => {
      state.companyKpi = action.payload
    },
    setJobs: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobs>
    ) => {
      state.jobs = action.payload
    },
    setJobsSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobsSortDirection>
    ) => {
      state.jobsSortDirection = action.payload
    },
    setLastJobId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastJobId>
    ) => {
      state.lastJobId = action.payload
    },
    setServicesInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.servicesInfo>
    ) => {
      state.servicesInfo = action.payload
    },
    setAppClients: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.appClients>
    ) => {
      state.appClients = action.payload
    },
    setWebhooks: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhooks>
    ) => {
      state.webhooks = action.payload
    },
    setWebhookLogs: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogs>
    ) => {
      state.webhookLogs = action.payload
    },
    setWebhookLogsSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogsSortDirection>
    ) => {
      state.webhookLogsSortDirection = action.payload
    },
    setLastWebhookLogId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastWebhookLogId>
    ) => {
      state.lastWebhookLogId = action.payload
    },
    setAllowedCountries: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.allowedCountries>
    ) => {
      state.allowedCountries = action.payload
    },
    resetUserSlice: () => initialState,
  },
})

export const getUserState = (state: { user: UserState }) => state.user
export const {
  setUser,
  setCompanyConsumption,
  setCompanyKpi,
  setJobs,
  setJobsSortDirection,
  setLastJobId,
  setServicesInfo,
  setAppClients,
  setWebhooks,
  setWebhookLogs,
  setWebhookLogsSortDirection,
  setLastWebhookLogId,
  setAllowedCountries,
  resetUserSlice,
} = UserSlice.actions
export default UserSlice.reducer
